// js/contact-form.js
// ===================================
// CONTACT FORM HANDLER - FRONTEND
// Profesional handler para el formulario de contacto GoPeruNow
// ===================================
import { notifications } from "./notifications.js";

class ContactFormManager {
  constructor() {
    this.form = null;
    this.submitBtn = null;
    this.btnText = null;
    this.btnLoading = null;
    this.messageContainer = null;
    this.isSubmitting = false;
  }

  init() {
    this.form = document.getElementById("contactForm");
    this.submitBtn = document.getElementById("contact-submit-btn");
    this.btnText = document.getElementById("contact-btn-text");
    this.btnLoading = document.getElementById("contact-btn-loading");
    this.messageContainer = document.getElementById("contact-form-message");

    if (!this.form) return;

    this.form.addEventListener("submit", (e) => this.handleSubmit(e));
    const inputs = this.form.querySelectorAll("input, select, textarea");
    inputs.forEach((input) => {
      input.addEventListener("blur", () => this.validateField(input));
      input.addEventListener("input", () => this.clearFieldError(input));
    });
  }

  validateField(field) {
    const value = field.value.trim();
    let isValid = true;
    let errorMessage = "";

    if (field.hasAttribute("required") && !value) {
      isValid = false;
      errorMessage = "Este campo es obligatorio";
    } else if (field.type === "email" && value) {
      // Validación simple de correo electrónico
      isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      if (!isValid)
        errorMessage = "Por favor ingresa una dirección de correo válida";
    }

    this.showFieldError(field, isValid, errorMessage);
    return isValid;
  }

  showFieldError(field, isValid, message) {
    field.classList.toggle("border-red-500", !isValid);
    field.classList.toggle("border-green-500", isValid && field.value.trim());
    field.classList.toggle("border-stone-300", isValid && !field.value.trim());

    let errorElement = field.parentNode.querySelector(".field-error");
    if (!isValid && message) {
      if (!errorElement) {
        errorElement = document.createElement("div");
        errorElement.className = "field-error text-red-500 text-sm mt-1";
        errorElement.textContent = message;
        field.parentNode.appendChild(errorElement);
      } else {
        errorElement.textContent = message;
      }
    } else if (errorElement) {
      errorElement.remove();
    }
  }

  clearFieldError(field) {
    field.classList.remove("border-red-500");
    const errorElement = field.parentNode.querySelector(".field-error");
    if (errorElement) errorElement.remove();
  }

  validateForm() {
    const requiredFields = this.form.querySelectorAll("[required]");
    let isFormValid = true;
    requiredFields.forEach((field) => {
      if (!this.validateField(field)) isFormValid = false;
    });
    return isFormValid;
  }

  collectFormData() {
    const formData = new FormData(this.form);
    return {
      first_name: formData.get("first_name")?.trim(),
      last_name: formData.get("last_name")?.trim(),
      email: formData.get("email")?.trim(),
      phone: formData.get("phone")?.trim(),
      subject: formData.get("subject"),
      message: formData.get("message")?.trim(),
      submission_time: new Date().toISOString(),
      user_agent: navigator.userAgent,
      referrer: document.referrer,
      page_url: window.location.href,
      timestamp: Date.now(),
    };
  }

  setLoadingState(isLoading) {
    this.submitBtn.disabled = isLoading;
    this.btnText.style.display = isLoading ? "none" : "inline";
    this.btnLoading.style.display = isLoading ? "inline" : "none";
    if (isLoading) {
      this.submitBtn.classList.add("opacity-75", "cursor-not-allowed");
    } else {
      this.submitBtn.classList.remove("opacity-75", "cursor-not-allowed");
    }
  }

  showMessage(message, type = "success") {
    // Toast en la esquina superior derecha
    notifications.show(message, type);

    if (!this.messageContainer) return;
    this.messageContainer.style.display = "block";
    this.messageContainer.className =
      "p-4 rounded-lg text-center font-semibold transition-all duration-300 " +
      (type === "success"
        ? "bg-green-50 text-green-800 border border-green-200"
        : "bg-red-50 text-red-800 border border-red-200");
    this.messageContainer.textContent = message;
  }

  clearMessage() {
    if (this.messageContainer) {
      this.messageContainer.style.display = "none";
      this.messageContainer.textContent = "";
    }
  }

  async handleSubmit(e) {
    e.preventDefault();
    if (this.isSubmitting) return;
    this.clearMessage();

    if (!this.validateForm()) {
      this.showMessage(
        "Por favor corrige los errores antes de enviar.",
        "error"
      );
      return;
    }

    this.isSubmitting = true;
    this.setLoadingState(true);

    try {
      const data = this.collectFormData();
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      const result = await response.json();

      if (result.success) {
        this.showMessage(result.message, "success");
        this.form.reset();
        const fields = this.form.querySelectorAll("input, select, textarea");
        fields.forEach((field) => {
          this.clearFieldError(field);
          field.classList.remove("border-green-500");
          field.classList.add("border-stone-300");
        });
      } else {
        this.showMessage(
          result.message || "Mensaje no enviado. Por favor intenta nuevamente.",
          "error"
        );
      }
    } catch (error) {
      this.showMessage(
        "No se pudo enviar tu mensaje en este momento. Por favor intenta nuevamente o contáctanos directamente al +51-982-373-883",
        "error"
      );
    } finally {
      this.isSubmitting = false;
      this.setLoadingState(false);
    }
  }
}

// Export singleton instance
export const contactFormManager = new ContactFormManager();
export default contactFormManager;
